import { z } from "zod";
import {
  EnrichedUserDataIdPublicSchema,
  EventParticipantIdPublicSchema,
  UserEnrichmentRequestIdPublicSchema,
} from "./branded-id.schemas.js";
import {
  EnrichmentRequestAdminStatusSchema,
  EnrichmentRequestStatusHistoryRowSchema,
} from "./enrichment-request-admin-common.schemas.js";

/**
 * Admin list row for a user enrichment request. `source` stays a string
 * (format-only, like the sibling enrichment transport schemas); timestamps are
 * coerced dates serialized to ISO on the wire.
 */
export const UserEnrichmentRequestRowSchema = z.object({
  idPublic: UserEnrichmentRequestIdPublicSchema,
  status: EnrichmentRequestAdminStatusSchema,
  source: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  retryCount: z.number().int(),
  errorMessage: z.string().nullable(),
  enrichedUserDataIdPublic: EnrichedUserDataIdPublicSchema.nullable(),
  /** Identifying input value for list rows: fullName || firstEmail || linkedInSlug */
  inputLabel: z.string().nullable(),
});

export type UserEnrichmentRequestRow = z.infer<
  typeof UserEnrichmentRequestRowSchema
>;

export const UserEnrichmentRequestInfoSchema = z.object({
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  linkedInUrl: z.string().nullable(),
  fullName: z.string().nullable(),
  companyName: z.string().nullable(),
  companyUrl: z.string().nullable(),
  additionalInfo: z.string().nullable(),
});

/**
 * Admin detail view of a user enrichment request. Extends the list row with
 * the request input, status-history timeline, canonical-id result, and the
 * public ids of the participants the request notifies on completion (resolved
 * from the request's internal notify map). Raw provider payload is omitted.
 */
export const UserEnrichmentRequestDetailViewSchema =
  UserEnrichmentRequestRowSchema.extend({
    externalRequestId: z.string().nullable(),
    canonicalIdType: z.string().nullable(),
    canonicalIdValue: z.string().nullable(),
    input: UserEnrichmentRequestInfoSchema,
    statusHistory: z.array(EnrichmentRequestStatusHistoryRowSchema),
    notifyParticipantIdsPublic: z.array(EventParticipantIdPublicSchema),
  });

export type UserEnrichmentRequestDetailView = z.infer<
  typeof UserEnrichmentRequestDetailViewSchema
>;

/**
 * Request body for PUT /admin/enrichment/user/trigger. Accepts either a batch
 * of participant public ids (the original participant-import path) and/or a
 * batch of existing request public ids (re-runs each request's notify
 * participants). At least one non-empty batch is required. Both run the same
 * createUserEnrichmentRequests path, so each participant comes back created /
 * joined (existing in-flight request) / skipped (fresh data) / ineligible.
 */
export const TriggerUserEnrichmentBodySchema = z
  .object({
    participantIdsPublic: z
      .array(EventParticipantIdPublicSchema)
      .min(1)
      .optional(),
    userEnrichmentRequestIdsPublic: z
      .array(UserEnrichmentRequestIdPublicSchema)
      .min(1)
      .optional(),
  })
  .refine(
    (body) =>
      body.participantIdsPublic !== undefined ||
      body.userEnrichmentRequestIdsPublic !== undefined,
    {
      message:
        "At least one of participantIdsPublic or userEnrichmentRequestIdsPublic must be provided",
    },
  );

export type TriggerUserEnrichmentBody = z.infer<
  typeof TriggerUserEnrichmentBodySchema
>;

export const UserEnrichmentTriggerResultSchema = z.object({
  participantIdPublic: EventParticipantIdPublicSchema,
  /** The created request, the joined in-flight request, or null when skipped/ineligible with no request */
  requestIdPublic: UserEnrichmentRequestIdPublicSchema.nullable(),
  /** CreateEnrichmentRequestResultStatus (ENQUEUED / SKIPPED_IN_FLIGHT_REQUEST / SKIPPED_FRESH_USER_DATA / INELIGIBLE / FEATURE_DISABLED) */
  status: z.string(),
});

export type UserEnrichmentTriggerResult = z.infer<
  typeof UserEnrichmentTriggerResultSchema
>;
